import { CommonModule } from '@angular/common';
import { Component, effect, input, signal, TemplateRef } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';

@Component({
  selector: 'abb-query',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, AbbIconComponent, TPipe],
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'commons' }],
  template: `
    @if (state().phase === 'loading') {
    <!-- prettier-ignore -->
    @if (loadingTemplate(); as loadingTemplate){
    <ng-container *ngTemplateOutlet="loadingTemplate"></ng-container>} @else {
    <div [class]="'flex items-center justify-center py-4 ' + loadingClass()">
      <mat-progress-spinner
        [diameter]="60"
        color="primary"
        mode="indeterminate"
      ></mat-progress-spinner>
    </div>
    } } @else if (state().phase === 'error'){
    <!-- prettier-ignore -->
    @if (errorTemplate(); as errorTemplate){
    <ng-container
      *ngTemplateOutlet="errorTemplate; context: { $implicit: state().error }"
    ></ng-container
    >} @else {
    <div
      [class]="'flex items-center gap-4 justify-center py-4 ' + errorClass()"
    >
      <abb-icon
        [name]="'abb/warning-circle-2'"
        [externalSize]="'text-7xl'"
        class="justify-center text-abb-error"
      />
      <div class="content-center text-center">
        {{ state().error?.toString?.() ?? ("An error occurred" | t) }}
      </div>
    </div>
    } } @else if (state().phase === 'success') {
    <ng-container class="min-h-screen w-full"
      *ngTemplateOutlet="
        successTemplate();
        context: { $implicit: state().value! }
      "
    ></ng-container>
    }
  `,
  styles: ``,
})
export class AbbQueryComponent<T> {
  loadingClass = input<string>('');
  errorClass = input<string>('');
  state = signal<
    | {
        phase: 'idle';
        value?: undefined;
        error?: undefined;
      }
    | {
        phase: 'loading';
        value?: undefined;
        error?: undefined;
      }
    | {
        phase: 'success';
        value: T;
        error?: undefined;
      }
    | {
        phase: 'error';
        value?: undefined;
        error: unknown;
      }
  >({ phase: 'loading' });
  query = input<Promise<T> | undefined>();
  loadingTemplate = input<TemplateRef<any> | undefined>();
  errorTemplate = input<TemplateRef<{ $implicit: unknown }> | undefined>();
  successTemplate = input.required<TemplateRef<{ $implicit: T }>>();

  constructor() {
    effect(
      () => {
        const curQuery = this.query();
        if (!curQuery) {
          this.state.set({ phase: 'idle' });
        } else {
          this.state.set({ phase: 'loading' });
          curQuery.then(
            (value) => this.state.set({ phase: 'success', value }),
            (error) => this.state.set({ phase: 'error', error })
          );
        }
      },
      { allowSignalWrites: true }
    );
  }
}
